import {
  createElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getMe, login as apiLogin, logout as apiLogout, signup as apiSignup } from '@/api/auth'
import type { User } from '@/api/auth'
import { getToken, removeToken, setToken } from '@/stores/auth'

interface AuthContextValue {
  user: User | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  setUser: (user: User) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = useQueryClient()

  useEffect(() => {
    const token = getToken()
    if (!token) {
      setIsLoading(false)
      return
    }
    getMe()
      .then(setUser)
      .catch(() => removeToken())
      .finally(() => setIsLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiLogin(email, password)
    setToken(res.token)
    // クエリキーにユーザー ID を含めていないため、前のユーザーのキャッシュを破棄する
    queryClient.clear()
    setUser(res.user)
  }, [queryClient])

  const signup = useCallback(async (email: string, password: string) => {
    const res = await apiSignup(email, password)
    setToken(res.token)
    queryClient.clear()
    setUser(res.user)
  }, [queryClient])

  const logout = useCallback(async () => {
    await apiLogout().catch(() => undefined)
    removeToken()
    setUser(null)
    queryClient.clear()
  }, [queryClient])

  const refreshUser = useCallback(async () => {
    const updated = await getMe()
    setUser(updated)
  }, [])

  const value = useMemo(
    () => ({ user, isLoading, login, signup, logout, refreshUser, setUser }),
    [user, isLoading, login, signup, logout, refreshUser],
  )

  return createElement(AuthContext.Provider, { value }, children)
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
